import { useEffect, useState } from 'react';
import type { Adapter } from '../lib/api.ts';
import { SCENARIOS } from '../lib/transports/simulator.ts';
import { describeTransportSupport } from '../lib/transports/types.ts';
import { grantedDeviceName } from '../lib/transports/webbluetooth.ts';
import type { ScanProgress } from '../lib/elm327.ts';
import type { TransportStatus } from '../lib/transports/types.ts';
import { transportLabel } from './AdapterTools.tsx';

interface Props {
  selected: Adapter | null;
  /** Opens adapter settings: choosing, adding, editing and testing. */
  onOpenSettings: () => void;
  /** Opens first-run setup, for somebody who has no adapter yet. */
  onOpenSetup: () => void;
  simScenario: string;
  onSimScenario: (id: string) => void;
  /** Present only once a scan has found codes worth clearing. */
  onClearCodes?: () => void;
  storedCodeCount?: number;
  live?: boolean;
  onToggleLive?: () => void;
  status: TransportStatus;
  busy: boolean;
  /** Counts down while the link is being kept alive after a scan. */
  holdSecondsLeft: number;
  progress: ScanProgress | null;
  onScan: () => void;
  onDisconnect: () => void;
}

export function ConnectionPanel(props: Props) {
  const { selected, onClearCodes, storedCodeCount = 0, live, onToggleLive, simScenario, onSimScenario, status, busy, progress } = props;

  const [device, setDevice] = useState<string | null>(grantedDeviceName());

  // The transport learns the name only when it connects, so this catches up
  // after a scan rather than guessing beforehand.
  useEffect(() => {
    const name = grantedDeviceName();
    if (name !== device) setDevice(name);
  });
  // Clearing codes is confirmed here rather than in a browser dialog, so the
  // stored-code count and the readiness warning stay on screen while it is read.
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [typed, setTyped] = useState('');

  const isSim = selected?.transport === 'sim';
  const support = selected ? describeTransportSupport(selected.transport) : { supported: true };

  return (
    <>
      {/* Only the adapter in use and a way into its settings. Choosing, adding,
          editing and the Wi-Fi tools used to sit here as three sections, which
          buried the one question that mattered on a first visit: has a reader
          been set up at all? */}
      <section className="stack">
        <div className="row">
          <span className="eyebrow" style={{ margin: 0, flex: 1 }}>
            Adapter
          </span>
          {selected && (
            <button className="tiny ghost" onClick={props.onOpenSettings}>
              Adapter settings
            </button>
          )}
        </div>

        {selected ? (
          <div className="adapter-current">
            <strong>{selected.name}</strong>
            <span className="small dim">
              {transportLabel(selected.transport)}
              {selected.transport === 'ble' && device ? ` · ${device}` : ''}
            </span>
          </div>
        ) : (
          <div className="adapter-missing">
            <span className="small">No adapter set up yet. Tell us how you connect to the car before you scan.</span>
            <button className="primary" onClick={props.onOpenSetup}>
              Set up adapter
            </button>
          </div>
        )}

        {!support.supported && <div className="banner warn small">{support.reason}</div>}

        {isSim && (
          <label className="field">
            <span>Simulated vehicle</span>
            <select value={simScenario} onChange={(e) => onSimScenario(e.target.value)}>
              {SCENARIOS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      {props.holdSecondsLeft > 0 && (
        <div className="banner info small">
          Connection held open for {props.holdSecondsLeft}s. Start live data now to keep it — Bluetooth adapters drop an
          idle link, so a little traffic is being sent until you do.
        </div>
      )}

      <section className="stack">
        <span className="eyebrow">Session</span>
        <div className="row wrap">
          <StatusPill status={status} busy={busy} />
        </div>
        {progress && (
          <>
            <div className="progress">
              <div style={{ width: `${(progress.done / progress.total) * 100}%` }} />
            </div>
            <span className="small dim">{progress.step}…</span>
          </>
        )}
        <div className="row wrap">
          {/* Both act on the live connection, so they belong beside the
              controls that open and close it rather than inside a result. */}
          <button className="primary" onClick={props.onScan} disabled={busy || !selected || !support.supported}>
            {busy ? 'Scanning…' : 'Scan vehicle'}
          </button>
          {onToggleLive && (
            <button className={live ? 'danger' : ''} onClick={onToggleLive} disabled={busy}>
              {live ? 'Stop stream' : 'Stream live'}
            </button>
          )}
          {onClearCodes && storedCodeCount > 0 && (
            <button
              className="danger"
              onClick={() => { setConfirmingClear(!confirmingClear); setTyped(''); }}
              disabled={busy}
              title={`${storedCodeCount} stored code(s)`}
            >
              Clear codes
            </button>
          )}
          <button className="ghost" onClick={props.onDisconnect} disabled={busy || status.state !== 'open'}>
            Disconnect
          </button>
        </div>
        {confirmingClear && onClearCodes && storedCodeCount > 0 && (
          <form
            className="person-confirm"
            onSubmit={(e) => {
              e.preventDefault();
              setConfirmingClear(false);
              setTyped('');
              onClearCodes();
            }}
          >
            <p className="small">
              This erases {storedCodeCount} stored code{storedCodeCount === 1 ? '' : 's'} and their
              freeze-frame data, and resets the readiness monitors. The car can fail an emissions
              test until it has completed a drive cycle.
            </p>
            {/* The phrase rather than the VIN. This is a routine job on a car
                already in the bay, and a 17-character VIN every time would only
                teach people to work around it. */}
            <p className="confirm-ask">
              Type <strong>clear codes</strong> below to confirm.
            </p>
            <div className="confirm-row">
              <input
                className="confirm-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="clear codes"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                aria-label="Type clear codes to confirm"
              />
              <button className="tiny danger" type="submit" disabled={busy || typed.trim().toLowerCase() !== 'clear codes'}>
                clear codes
              </button>
              <button
                className="tiny ghost"
                type="button"
                disabled={busy}
                onClick={() => { setConfirmingClear(false); setTyped(''); }}
              >
                cancel
              </button>
            </div>
          </form>
        )}
        {status.detail && <span className="small dim">{status.detail}</span>}
      </section>

    </>
  );
}

function StatusPill({ status, busy }: { status: TransportStatus; busy: boolean }) {
  // "idle" is the state before anything has been attempted, but the word reads
  // as connected-but-quiet, which is the opposite of what it means. Both
  // not-connected states say so; they differ only in whether a connection ever
  // existed.
  const map: Record<string, { cls: string; label: string }> = {
    idle: { cls: '', label: 'not connected' },
    connecting: { cls: 'info live', label: 'connecting' },
    open: { cls: 'ok live', label: 'connected' },
    closing: { cls: '', label: 'closing' },
    closed: { cls: '', label: 'disconnected' },
    error: { cls: 'crit', label: 'error' },
  };
  const entry = map[status.state] ?? map.idle;
  return (
    <span className={`pill ${entry.cls} ${busy ? 'live' : ''}`}>
      <i className="dot" />
      {entry.label}
    </span>
  );
}
